import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cardBackImage from '../assets/thirdBackSM.jpeg';
import { getSuitEmoji } from '../utils/cardHelpers';

// Animation and layout constants
const CARD_SPREAD_RADIUS = 180; // Distance of cards from center (px)
const CARD_REVEAL_DELAY = 400; // Time before card flies after reveal (ms)
const CARD_FLY_DURATION = 600; // Duration of flying animation (ms)
const CARD_WIDTH_OFFSET = -25; // Half of card width for centering (px)
const CARD_HEIGHT_OFFSET = -42.5; // Half of card height for centering (px)

/**
 * CardSpread component displays 78 tarot cards in a fanned-out spread
 * Users can click cards to select them for their reading
 */
export default function CardSpread({ cards, onCardSelect, selectedCards, onSwitchToPhysicalDeck }) {
  const [revealedCards, setRevealedCards] = useState(new Set());
  const [flyingCards, setFlyingCards] = useState(new Set());
  const [centerCard, setCenterCard] = useState(null); // Card displayed in center
  
  // Hide the spread after 2 cards are selected
  if (selectedCards.length >= 2) {
    return null;
  }

  // Helper to create a delay promise
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleCardClick = async (card, index) => {
    // Reveal the card
    setRevealedCards(prev => new Set([...prev, index]));
    
    // Wait for reveal animation to complete
    await delay(CARD_REVEAL_DELAY);
    
    if (selectedCards.length === 0) {
      // First card - add to flying, then move to center
      setFlyingCards(prev => new Set([...prev, index]));
      
      // Wait for fly animation to complete
      await delay(CARD_FLY_DURATION);
      
      setCenterCard(card);
      onCardSelect(card);
    } else {
      // Second card - fly away
      setFlyingCards(prev => new Set([...prev, index]));
      onCardSelect(card);
    }
  };

  const isCardSelected = (cardId) => {
    return selectedCards.some(sc => sc?.id === cardId);
  };

  // Calculate rotation and position for each card in a circular/spiral layout
  const getCardStyle = (index, total) => {
    // Create a circular spread
    const angle = (index / total) * 360; // Full circle
    const radius = CARD_SPREAD_RADIUS; // Distance from center
    
    // Calculate position on circle
    const radians = (angle - 90) * (Math.PI / 180); // Start from top
    const x = Math.cos(radians) * radius;
    const y = Math.sin(radians) * radius;
    
    // Rotate card to face outward from center
    const cardRotation = angle;
    
    return {
      transform: `
        translateX(${x}px) 
        translateY(${y}px) 
        rotate(${cardRotation}deg)
      `,
      zIndex: index,
      left: '50%',
      marginLeft: `${CARD_WIDTH_OFFSET}px`,
      top: '50%',
      marginTop: `${CARD_HEIGHT_OFFSET}px`
    };
  };

  return (
    <div className="card-spread-container">
      <div className="text-center mb-4">
        <h3 className="heading-tertiary">✦ The Sacred Deck ✦</h3>
        {selectedCards.length === 0 ? (
          <p className="text-xs text-amber-400 opacity-70">
            Click any card to reveal and select your first card
          </p>
        ) : (
          <p className="text-xs text-amber-400 opacity-70">
            Now choose your second card
          </p>
        )}
        
        {/* Button to switch to physical deck */}
        {onSwitchToPhysicalDeck && (
          <button
            onClick={onSwitchToPhysicalDeck}
            className="mt-4 px-4 py-2 text-sm bg-purple-900/40 hover:bg-purple-800/60 text-amber-300 rounded-lg border border-amber-600/30 transition-all duration-300 hover:border-amber-500/50"
          >
            🃏 Use My Own Deck
          </button>
        )}
      </div>
      
      <div className="card-spread-wrapper">
        <div className="card-spread">
          {/* Center card display for first selected card */}
          {centerCard && (
            <div className="center-card-display">
              <div className="center-card">
                <div className="card-front-content">
                  <span className="text-sm font-semibold">
                    {centerCard.suit && `${getSuitEmoji(centerCard.suit)} `}
                    {centerCard.name}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {cards.map((card, index) => {
            const isRevealed = revealedCards.has(index);
            const isSelected = isCardSelected(card.id);
            const isFlying = flyingCards.has(index);
            const isFirstCard = selectedCards.length === 1 && isFlying;
            
            // Hide selected cards after they fly away
            if (isSelected && isFlying) {
              return null;
            }
            
            return (
              <div
                key={`${card.id}-${index}`}
                className={`tarot-card-back ${isRevealed ? 'revealed' : ''} ${isSelected ? 'selected' : ''} ${isFlying ? (isFirstCard ? 'flying-to-center' : 'flying') : ''}`}
                style={getCardStyle(index, cards.length)}
                onClick={() => !isSelected && !isFlying && handleCardClick(card, index)}
              >
                <div className="card-inner">
                  {/* Card Back */}
                  <div className="card-face card-back-face">
                    <img 
                      src={cardBackImage} 
                      alt="Card back" 
                      className="card-back-image"
                    />
                  </div>
                  
                  {/* Card Front (revealed) */}
                  <div className="card-face card-front-face">
                    <div className="card-front-content">
                      <span className="text-xs font-semibold">
                        {card.suit && `${getSuitEmoji(card.suit)} `}
                        {card.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

CardSpread.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    suit: PropTypes.string,
  })).isRequired,
  onCardSelect: PropTypes.func.isRequired,
  selectedCards: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    orientation: PropTypes.string,
  })).isRequired,
  onSwitchToPhysicalDeck: PropTypes.func,
};

CardSpread.defaultProps = {
  onSwitchToPhysicalDeck: null,
};
